import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Switch } from "./ui/switch";

export const ampm = (t) => new Date(`2000-01-01T${t}:00`).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
export const istDT = (s) => new Date(s).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" });

export const List = ({ title, rows, tid, render }) => (
  <div className="mb-3" data-testid={tid}>
    <div className="text-xs text-muted-foreground mb-1">{title} ({rows.length})</div>
    {rows.length === 0 ? <p className="text-sm text-muted-foreground">None</p> : (
      <ul className="text-sm space-y-1">{rows.slice(0, 10).map((r) => <li key={r.id} className="border-t border-border/50 pt-1">{render(r)}</li>)}
        {rows.length > 10 && <li className="text-xs text-muted-foreground">and {rows.length - 10} more</li>}</ul>)}
  </div>
);

const Preview = ({ b }) => (
  <div className="border p-3 mt-3" data-testid="partner-brief-preview">
    {b.all_clear ? <p className="text-sm font-medium" data-testid="partner-brief-all-clear">{b.all_clear}</p> : (<>
      <List title="Late steps" tid="partner-brief-late" rows={b.late} render={(x) => `${x.uid} · ${x.client} · ${x.step} · ${x.owner} · ${x.days_late} day(s) late`} />
      <List title="Waiting for approval" tid="partner-brief-approvals" rows={b.approvals} render={(x) => `${x.uid || ""} · ${x.client} · ${x.kind} · by ${x.by}`} />
      <List title="Dispatching today" tid="partner-brief-dispatches" rows={b.dispatches} render={(x) => `${x.uid} · ${x.client} · ${x.what}`} />
    </>)}
  </div>
);

const recipientText = (r) => {
  if (!r) return "dhaval@axiomviscom.in";
  return `${r.name} (${r.email})${r.status !== "active" ? " (account switched off, so nothing is sent)" : ""}`;
};

const historyLine = (h) => {
  if (!h.sent_at) return `Not sent: ${h.skipped}`;
  return `${h.summary}${h.email_ok ? "" : " · email failed, app alert sent"}`;
};

const History = ({ items }) => (items.length > 0 ? (
  <div className="mt-3 text-xs text-muted-foreground" data-testid="partner-brief-history">
    Last sent: {items.map((h) => <div key={h.date}>{istDT(h.sent_at || h.at)} · {historyLine(h)}</div>)}</div>
) : null);

const Controls = ({ f, setF, times, changed, save, preview }) => (
  <div className="flex flex-wrap items-center gap-3">
    <label className="flex items-center gap-2 text-sm"><Switch data-testid="partner-brief-enabled" checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} />{f.enabled ? "On" : "Stopped"}</label>
    <select data-testid="partner-brief-time" value={f.time} disabled={!f.enabled} onChange={(e) => setF({ ...f, time: e.target.value })} className="bg-muted/60 border h-9 px-2 text-sm">
      {times.map((t) => <option key={t} value={t}>{ampm(t)}</option>)}</select>
    <button data-testid="partner-brief-save" disabled={!changed} onClick={save} className="border border-primary text-primary h-9 px-4 text-sm disabled:opacity-40">Save</button>
    <button data-testid="partner-brief-preview-button" onClick={preview} className="border h-9 px-4 text-sm">See today's brief</button>
  </div>
);

export const PartnerBriefCard = () => {
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const [pv, setPv] = useState(null);
  const load = useCallback(() => api.get("/partner-brief").then((r) => { setD(r.data); setF(r.data.settings); }).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!d || !f) return null;
  const save = () => api.put("/partner-brief", { enabled: f.enabled, time: f.time })
    .then(() => { toast.success(f.enabled ? `Partner brief set for ${ampm(f.time)} every day` : "Partner brief stopped"); load(); }).catch((e) => toast.error(errMsg(e)));
  const preview = () => api.get("/partner-brief/preview").then((r) => setPv(r.data)).catch((e) => toast.error(errMsg(e)));
  const changed = f.enabled !== d.settings.enabled || f.time !== d.settings.time;
  return (
    <div className="ax-panel p-4 mb-4" data-testid="partner-brief-card">
      <div className="ax-label mb-1">Partner morning brief</div>
      <p className="text-sm text-muted-foreground mb-3" data-testid="partner-brief-recipient">
        Mumbai's late steps, approvals waiting and today's dispatches, by email and app alert, every day.
        Goes to {recipientText(d.recipient)}.
      </p>
      <Controls f={f} setF={setF} times={d.times} changed={changed} save={save} preview={preview} />
      {pv && <Preview b={pv} />}
      <History items={d.history} />
    </div>
  );
};
